/**
 * HeroPage — marketing landing view.
 *
 * Extracted from App.js during the P1 refactor. Zero behavioural changes:
 * same props (onDismiss, onSignInGoogle), same test-ids, same DOM.
 *
 * The two SVG icons live here rather than in a shared /icons directory
 * because they are only used by the hero page's SSO buttons.
 */
import React from "react";

export function HeroPage({ onDismiss, onSignInGoogle, authError }) {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const startGoogle = onSignInGoogle || onDismiss;
  return (
    <div className="beacon-hero" data-testid="hero-page">
      <div className="beacon-hero-grain" aria-hidden="true" />

      {authError && (
        <div className="beacon-hero-auth-error" data-testid="auth-error-banner"
          role="alert">
          <strong>Sign-in didn&apos;t complete:</strong> {authError}.{" "}
          Please try signing in again — a fresh attempt usually resolves it.
          <button
            type="button"
            className="beacon-hero-auth-error-retry"
            data-testid="auth-error-retry"
            onClick={startGoogle}
          >
            Try again →
          </button>
        </div>
      )}

      {/* Sticky topbar — always offers sign-in */}
      <header className="beacon-hero-topbar beacon-hero-topbar-sticky">
        <div className="beacon-brand">
          <span className="beacon-wordmark" data-testid="hero-wordmark">
            BEACON
          </span>
          <span className="beacon-divider">//</span>
          <span className="beacon-tagline">H&amp;S COMMS, CALIBRATED.</span>
        </div>
        <div className="beacon-hero-topbar-actions">
          <button
            type="button"
            className="beacon-hero-topbar-link"
            data-testid="hero-nav-engines"
            onClick={() => scrollTo("hero-engines")}
          >
            How it works
          </button>
          <button
            type="button"
            className="beacon-hero-topbar-link"
            data-testid="hero-nav-outputs"
            onClick={() => scrollTo("hero-outputs")}
          >
            Outputs
          </button>
          <a
            className="beacon-hero-topbar-link"
            data-testid="hero-nav-pricing"
            href="/pricing"
          >
            Pricing
          </a>
          <button
            type="button"
            className="beacon-hero-topbar-link"
            data-testid="hero-nav-why"
            onClick={() => scrollTo("hero-why")}
          >
            Why Beacon
          </button>
          <button
            type="button"
            className="beacon-hero-topbar-cta"
            data-testid="hero-topbar-signin"
            onClick={startGoogle}
          >
            Sign in →
          </button>
        </div>
      </header>

      {/* SECTION 1 — STATEMENT */}
      <section className="beacon-hero-statement" data-testid="hero-statement">
        <div className="beacon-hero-statement-inner">
          <div className="beacon-hero-eyebrow">
            FOR THE H&amp;S MANAGER WHO HAS TO ACTUALLY SHIP THIS STUFF
          </div>
          <h1 className="beacon-hero-title">
            <span className="beacon-hero-title-row">
              Your <span className="beacon-hero-title-em">year-long</span>{" "}
              H&amp;S comms plan.
            </span>
            <span className="beacon-hero-title-row">
              And the <span className="beacon-hero-title-em">one-off</span>{" "}
              you needed yesterday.
            </span>
          </h1>
          <p className="beacon-hero-lede">
            Beacon is two engines in one. A 12-month themed plan — 48 weekly
            topics, ready to pin to the wall. And a drop-a-doc workflow that
            turns an incident report, a procedure, or a regulator&apos;s
            update into an A4 poster, a toolbox handout, a branded email and
            an intranet article — in minutes, in your industry&apos;s voice,
            on your jurisdiction&apos;s rules.
          </p>
          <div className="beacon-hero-cta-row">
            <button
              type="button"
              className="beacon-hero-cta-primary"
              data-testid="hero-cta-primary"
              onClick={() => scrollTo("hero-engines")}
            >
              See how it works
              <span aria-hidden="true">↓</span>
            </button>
            <button
              type="button"
              className="beacon-hero-cta-ghost"
              data-testid="hero-cta-secondary"
              onClick={() => scrollTo("hero-signin")}
            >
              Or sign in →
            </button>
          </div>
          <div className="beacon-hero-tag-row">
            <span className="beacon-hero-tag">Australian-vernacular</span>
            <span className="beacon-hero-tag">Every state &amp; territory</span>
            <span className="beacon-hero-tag">12-month plan + ad-hoc</span>
            <span className="beacon-hero-tag">Editable PPTX &amp; HTML</span>
          </div>
        </div>
      </section>

      {/* SECTION 2 — STATS STRIP */}
      <section className="beacon-hero-stats" data-testid="hero-stats">
        <div className="beacon-hero-stat" data-testid="hero-stat-themes">
          <div className="beacon-hero-stat-num">12</div>
          <div className="beacon-hero-stat-label">
            Monthly H&amp;S themes — one focus per month, all year
          </div>
        </div>
        <div className="beacon-hero-stat" data-testid="hero-stat-topics">
          <div className="beacon-hero-stat-num">48</div>
          <div className="beacon-hero-stat-label">
            Weekly topics on the calendar — one per toolbox session
          </div>
        </div>
        <div className="beacon-hero-stat" data-testid="hero-stat-campaigns">
          <div className="beacon-hero-stat-num">33</div>
          <div className="beacon-hero-stat-label">
            Australian campaigns built in — R U OK? Day, National Safe Work
            Month, National Road Safety Week, Movember, and many more
          </div>
        </div>
        <div className="beacon-hero-stat" data-testid="hero-stat-jurisdictions">
          <div className="beacon-hero-stat-num">8</div>
          <div className="beacon-hero-stat-label">
            States &amp; territories covered — NSW, VIC, QLD, WA, SA, TAS,
            ACT, NT — WHS or Victorian OHS, automatically
          </div>
        </div>
        <div className="beacon-hero-stat" data-testid="hero-stat-formats">
          <div className="beacon-hero-stat-num">4</div>
          <div className="beacon-hero-stat-label">
            Polished output formats from one source — poster, toolbox, email,
            intranet article
          </div>
        </div>
      </section>

      {/* SECTION 3 — TWIN ENGINES */}
      <section
        className="beacon-hero-engines"
        id="hero-engines"
        data-testid="hero-engines"
      >
        <div className="beacon-hero-section-head">
          <div className="beacon-hero-eyebrow">TWO ENGINES, ONE VOICE</div>
          <h2 className="beacon-hero-section-title">
            Plan the year. Handle the week that just blew up.
          </h2>
          <p className="beacon-hero-section-lede">
            Most safety platforms make you choose. Beacon does both — without
            making the comms sound like two different companies.
          </p>
        </div>

        <div className="beacon-hero-engine-grid">
          <article
            className="beacon-hero-engine"
            data-testid="hero-engine-plan"
          >
            <div className="beacon-hero-engine-tag">THE PLAN</div>
            <h3 className="beacon-hero-engine-title">
              A 12-month H&amp;S comms calendar that thinks like an H&amp;S
              manager.
            </h3>
            <p className="beacon-hero-engine-body">
              12 monthly themes — plant, manual handling, mental health,
              electrical, working at height, fatigue, psychosocial, and the
              rest. Each theme drops 4 weekly topics straight onto your
              calendar. National campaigns fork in automatically — R U OK?
              Day, National Safe Work Month, National Road Safety Week.
              Override any week with your own topic and Beacon keeps the
              voice consistent.
            </p>
            <ul className="beacon-hero-engine-features">
              <li>Industry-relevance badges so Construction sees Construction first</li>
              <li>33 national campaigns ready to fork in — pick the ones you run</li>
              <li>Print-the-year calendar as an A3 PPTX — pin it on the noticeboard</li>
              <li>Append-only override history — no quiet rewrites of last year&apos;s plan</li>
            </ul>
          </article>

          <article
            className="beacon-hero-engine beacon-hero-engine-alt"
            data-testid="hero-engine-quick"
          >
            <div className="beacon-hero-engine-tag">THE ONE-OFF</div>
            <h3 className="beacon-hero-engine-title">
              An incident landed at 4pm. The pack is on screens by 4:20.
            </h3>
            <p className="beacon-hero-engine-body">
              Drop the investigation report, the regulator&apos;s update, the
              new procedure — Beacon reads it, strips the names and dates,
              builds a topic, and ships a four-format pack. Tell it what
              angle you want (&quot;Make this a lessons-learned for
              supervisors&quot;) and it follows the brief.
            </p>
            <ul className="beacon-hero-engine-features">
              <li>PDF, DOCX or scanned image — OCR fallback handles the rest</li>
              <li>Names, dates and site identifiers stripped automatically</li>
              <li>Instructions box: tell Beacon the angle, audience, what to avoid</li>
              <li>Same four formats. Same voice. No re-writing for each channel.</li>
            </ul>
          </article>
        </div>
      </section>

      {/* SECTION 3.5 — MEET LUMA */}
      <section className="beacon-hero-luma" data-testid="hero-luma">
        <div className="beacon-hero-luma-inner">
          <div className="beacon-hero-luma-badge" aria-hidden="true">✦</div>
          <div className="beacon-hero-luma-copy">
            <div className="beacon-hero-eyebrow beacon-hero-luma-eyebrow">
              MEET LUMA — YOUR SAFETY COMMS OFFSIDER
            </div>
            <h2 className="beacon-hero-section-title">
              An AI offsider that plans, builds and reviews with you.
            </h2>
            <p className="beacon-hero-luma-body">
              Luma lives inside Beacon. Ask it to reshape your annual
              calendar, swap a theme, or build a custom week — it proposes
              the change and waits for your say-so before touching
              anything. Built something already? Luma reviews your
              finished packs for tone, clarity and jurisdiction fit, then
              hands you a paste-ready brief to regenerate anything that
              misses the mark.
            </p>
            <div className="beacon-hero-tag-row">
              <span className="beacon-hero-tag">Shapes your calendar with you</span>
              <span className="beacon-hero-tag">Reviews packs — quality &amp; compliance</span>
              <span className="beacon-hero-tag">Never changes a thing without your OK</span>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4 — FOUR OUTPUTS */}
      <section
        className="beacon-hero-outputs"
        id="hero-outputs"
        data-testid="hero-outputs"
      >
        <div className="beacon-hero-section-head">
          <div className="beacon-hero-eyebrow">FOUR FORMATS · ONE SOURCE</div>
          <h2 className="beacon-hero-section-title">
            One pack hits the wall, the toolbox, the inbox and the intranet.
          </h2>
          <p className="beacon-hero-section-lede">
            You never have to write the same message four different ways.
            Beacon does — and each output is shaped for the channel it lives
            in.
          </p>
        </div>

        <div className="beacon-hero-output-grid">
          <div className="beacon-hero-output" data-testid="hero-output-poster">
            <div className="beacon-hero-output-icon" aria-hidden="true">
              <div className="beacon-hero-output-icon-poster" />
            </div>
            <h4>A4 Poster PPTX</h4>
            <p>
              The monthly theme on the wall. Built A4 so it prints on any
              office printer — scale up to A3 for the crib room. One
              headline, one sub-line, three &quot;looks like here&quot;
              points, your logo top-right.
            </p>
          </div>
          <div className="beacon-hero-output" data-testid="hero-output-toolbox">
            <div className="beacon-hero-output-icon" aria-hidden="true">
              <span /><span /><span /><span /><span />
            </div>
            <h4>3-page Toolbox PPTX</h4>
            <p>
              The weekly topic within that theme — a pre-start handout sized
              for the morning huddle. One page per beat: the hazard, the
              friction point, what to do, what to watch, and a conversation
              prompt to close. Print it, hand it out, run the talk.
            </p>
          </div>
          <div className="beacon-hero-output" data-testid="hero-output-email">
            <div className="beacon-hero-output-icon" aria-hidden="true">
              <div className="beacon-hero-output-icon-email">@</div>
            </div>
            <h4>Branded Email</h4>
            <p>
              HTML that renders cleanly in Outlook, Gmail and most intranet
              email tools. Subject line written for an open, not a sigh.
              Markdown and plain-text fallbacks shipped alongside so you can
              copy-paste anywhere.
            </p>
          </div>
          <div className="beacon-hero-output" data-testid="hero-output-intranet">
            <div className="beacon-hero-output-icon" aria-hidden="true">
              <div className="beacon-hero-output-icon-article">¶</div>
            </div>
            <h4>Intranet Article</h4>
            <p>
              Long-form HTML with standfirst, pull-quote, tags and a clear
              &quot;what to do next&quot; list. Markdown also ships if your
              CMS prefers it. Paste straight into SharePoint, Confluence,
              Workplace, or whatever you run.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 5 — WHY BEACON */}
      <section
        className="beacon-hero-why"
        id="hero-why"
        data-testid="hero-why"
      >
        <div className="beacon-hero-section-head">
          <div className="beacon-hero-eyebrow">WHY BEACON</div>
          <h2 className="beacon-hero-section-title">
            Built for the supervisor who actually has to read it.
          </h2>
        </div>

        <div className="beacon-hero-why-grid">
          <div className="beacon-hero-why-item" data-testid="hero-why-vernacular">
            <div className="beacon-hero-why-num">A</div>
            <div className="beacon-hero-why-body">
              <h4>No corporate filler.</h4>
              <p>
                Beacon&apos;s voice is operational, Australian, and writes for
                the crew on smoko — not the board pack. &quot;Stakeholder
                synergies&quot; never appear. Ever.
              </p>
            </div>
          </div>
          <div className="beacon-hero-why-item" data-testid="hero-why-jurisdiction">
            <div className="beacon-hero-why-num">B</div>
            <div className="beacon-hero-why-body">
              <h4>Jurisdiction-aware by default.</h4>
              <p>
                Victoria runs on the OHS Act 2004 and WorkSafe Victoria.
                Every other state and territory runs on the Model WHS Act
                and Codes via SafeWork Australia. Pick yours — Beacon
                routes the language, the framework references and the
                regulator names automatically.
              </p>
            </div>
          </div>
          <div className="beacon-hero-why-item" data-testid="hero-why-industry">
            <div className="beacon-hero-why-num">C</div>
            <div className="beacon-hero-why-body">
              <h4>Industry-tuned.</h4>
              <p>
                The hazards a Construction site cares about aren&apos;t the
                hazards a Healthcare clinic cares about. Beacon surfaces the
                relevant ones first, and tones the language to match.
              </p>
            </div>
          </div>
          <div className="beacon-hero-why-item" data-testid="hero-why-brand">
            <div className="beacon-hero-why-num">D</div>
            <div className="beacon-hero-why-body">
              <h4>Your brand on. Your editor still in charge.</h4>
              <p>
                Upload your logo once in the Context Engine and it lands on
                every PPTX cover, poster corner, email header and intranet
                article. Every pack downloads as editable PPTX and HTML —
                nothing locked. Add your own photos, swap the layout,
                tweak the copy in the tool you already use.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6 — FINAL CTA / SIGN-IN */}
      <section
        className="beacon-hero-signin"
        id="hero-signin"
        data-testid="hero-signin"
      >
        <div className="beacon-hero-signin-inner">
          <div className="beacon-hero-signin-copy">
            <div className="beacon-hero-eyebrow">START WHEN YOU&apos;RE READY</div>
            <h2 className="beacon-hero-signin-title">
              Take Beacon for a spin.<br />
              The first pack is on us.
            </h2>
            <p className="beacon-hero-signin-lede">
              Sign in with your work account and Beacon remembers your
              context, your brand, your plan. No card. No demo call. No
              &quot;book a strategy session&quot; nonsense.
            </p>
            <ul className="beacon-hero-signin-trust">
              <li>✓ We never read your email, calendar or files</li>
              <li>✓ Your uploads are processed and dropped — not stored as training data</li>
              <li>✓ Australian-built, Australian-hosted, Australian-vernacular</li>
            </ul>
          </div>

          <div className="beacon-hero-auth" aria-label="Sign in">
            <div className="beacon-hero-auth-eyebrow">SIGN IN</div>
            <h3 className="beacon-hero-auth-title">Pick your work account.</h3>

            <button
              type="button"
              className="beacon-hero-sso"
              data-testid="hero-sso-google"
              onClick={startGoogle}
            >
              <GoogleIcon />
              <span className="beacon-hero-sso-label">
                Continue with Google Workspace
              </span>
            </button>

            <button
              type="button"
              className="beacon-hero-sso beacon-hero-sso-disabled"
              data-testid="hero-sso-microsoft"
              disabled
              title="Microsoft sign-in is coming soon — use Google for now."
            >
              <MicrosoftIcon />
              <span className="beacon-hero-sso-label">
                Continue with Microsoft 365
                <small className="beacon-hero-sso-note"> — coming soon</small>
              </span>
            </button>

            <p className="beacon-hero-footnote">
              Beacon is in a closed pilot — new accounts are activated by an
              administrator after sign-up.{" "}
              <a href="/pricing" data-testid="hero-pricing-inline-link">
                See pricing →
              </a>
            </p>
          </div>
        </div>
      </section>

      <footer className="beacon-hero-footer">
        <span>Beacon</span>
        <span>Australian-built, Australian-vernacular</span>
      </footer>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg
      className="beacon-hero-sso-icon-svg"
      viewBox="0 0 48 48"
      aria-hidden="true"
    >
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3.1 0 5.8 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.2C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-8L6 33C9.4 39.6 16.1 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.6l6.2 5.2c-.4.4 6.6-4.8 6.6-14.8 0-1.3-.1-2.4-.4-3.5z"/>
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg
      className="beacon-hero-sso-icon-svg"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <rect x="1" y="1" width="10" height="10" fill="#F25022" />
      <rect x="13" y="1" width="10" height="10" fill="#7FBA00" />
      <rect x="1" y="13" width="10" height="10" fill="#00A4EF" />
      <rect x="13" y="13" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}
